/**
 * Wait between reconnects of a live stream (boards.subscribe). Shared by the board page and the
 * background activity, so a stream the server keeps closing is asked for less and less often.
 */
import { retryDelayMs } from "@rakazo/core";

/** First wait after a drop, and the wait again once the stream delivered an event. */
export const STREAM_RETRY_FIRST_MS = 1_000;

/** Longest own wait; the same as the 15 s polling that covers a stream that is down. */
export const STREAM_RETRY_MAX_MS = 15_000;

/** Growing reconnect wait for one stream loop; see createStreamBackoff. */
export type StreamBackoff = {
  /** Wait before the next attempt after `failure` (undefined: the stream ended); grows the next one. */
  next(failure: unknown): number;
  /** The stream delivered an event, so the next drop waits STREAM_RETRY_FIRST_MS again. */
  reset(): void;
};

/**
 * Doubles from 1 s up to `maxMs` with every drop and starts over after an event. A wait the
 * server asked for (Retry-After on the error) is used as is, without growing the own one.
 */
export function createStreamBackoff(maxMs = STREAM_RETRY_MAX_MS): StreamBackoff {
  let own = STREAM_RETRY_FIRST_MS;
  return {
    next(failure) {
      // retryDelayMs with 0 returns only what the server asked for.
      const asked = retryDelayMs(failure, 0);
      if (asked > 0) return asked;
      const wait = own;
      own = Math.min(own * 2, maxMs);
      return wait;
    },
    reset() {
      own = STREAM_RETRY_FIRST_MS;
    },
  };
}
